import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { CatalogService } from '../../../../service/catalogService';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import ImportResultDialog from './ImportResultDialog';

const CatalogFrameImportDialog = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [columns, setColumns] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  // Define available model fields for mapping
  // TODO: should we fetch this from the backend instead of hardcoding?
  const modelFields = useMemo(() => [
    { key: 'name', label: 'Product Name', required: false },
    { key: 'sku', label: 'SKU' },
    { key: 'modelNumber', label: 'Model Number', required: true },
    { key: 'upcOrEan', label: 'UPC/EAN' },
    { key: 'gender', label: 'Gender' },
    { key: 'year', label: 'Year' },
    { key: 'a', label: 'A (Lens Width)' },
    { key: 'b', label: 'B (Bridge Width)' },
    { key: 'dbl', label: 'DBL (Temple Length)' },
    { key: 'temple', label: 'Temple' },
    { key: 'ed', label: 'ED (Effective Diameter)' },
    { key: 'frameType', label: 'Frame Type' },
    { key: 'colorName', label: 'Color Name' },
    { key: 'colorCode', label: 'Color Code' },
    { key: 'sizeCode', label: 'Size Code' },
    { key: 'brandSlug', label: 'Brand Slug', required: true },
    { key: 'shapeKey', label: 'Shape Key' },
    { key: 'materialKey', label: 'Material Key' }
  ], []);

  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setError('');
    setColumns([]);
    setMapping({});
    setPreviewData([]);

    // Parse file to get columns
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let parsedColumns = [];
        let data = [];

        if (selectedFile.name.endsWith('.csv')) {
          // Parse CSV
          const csv = Papa.parse(e.target.result, {
            header: true,
            skipEmptyLines: true
          });
          parsedColumns = csv.meta.fields || [];
          data = csv.data.slice(0, 5); // Preview first 5 rows
        } else if (selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
          // Parse Excel
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            parsedColumns = jsonData[0];
            data = jsonData.slice(1, 6).map(row => {
              const obj = {};
              parsedColumns.forEach((col, idx) => {
                obj[col] = row[idx] || '';
              });
              return obj;
            });
          }
        } else {
          setError('Unsupported file format. Please upload CSV or Excel file.');
          return;
        }

        setColumns(parsedColumns);
        setPreviewData(data);

        // Initialize mapping with auto-suggestions using exact and partial matches
        const initialMapping = {};
        parsedColumns.forEach(col => {
          const normalizedCol = col.toLowerCase().trim().replace(/[^a-z0-9]/g, '');
          
          // Try exact match first
          let matchedField = modelFields.find(field => {
            const normalizedKey = field.key.toLowerCase().replace(/[^a-z0-9]/g, '');
            return normalizedKey === normalizedCol;
          });
          
          // If no exact match, try common aliases
          if (!matchedField) {
            const aliases = {
              'brand': ['brandslug', 'brandname'],
              'modelnumber': ['model', 'modelnumber', 'modelno'],
              'upcorean': ['upc', 'ean', 'barcode'],
              'temple': ['tmpl', 'temple', 'templelength'],
              'frametype': ['type', 'frametype'],
              'colorname': ['color', 'colorname'],
              'shapekey': ['shape', 'frameshape'],
              'materialkey': ['material', 'framematerial']
            };
            
            matchedField = modelFields.find(field => {
              const fieldKey = field.key.toLowerCase().replace(/[^a-z0-9]/g, '');
              const fieldAliases = aliases[fieldKey] || [];
              return fieldAliases.includes(normalizedCol);
            });
          }
          
          if (matchedField) {
            initialMapping[col] = matchedField.key;
          }
        });
        setMapping(initialMapping);

      } catch (err) {
        setError('Error parsing file. Please check the file format.');
        console.error('File parsing error:', err);
      }
    };

    if (selectedFile.name.endsWith('.csv')) {
      reader.readAsText(selectedFile);
    } else {
      reader.readAsBinaryString(selectedFile);
    }
  }, [modelFields]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleMappingChange = (column, fieldKey) => {
    setMapping(prev => ({
      ...prev,
      [column]: fieldKey
    }));
  };

  const importMutation = useMutation(
    async ({ file, mapping }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      return CatalogService.importFrames(formData);
    },
    {
      onSuccess: (response) => {
        const data = response?.data || response;
        setImportResult(data);
        setResultDialogOpen(true);
        queryClient.invalidateQueries('catalogFrames');
      },
      onError: (error) => {
        toast.error(error.message || 'Import failed');
      }
    }
  );

  const handleImport = () => {
    // Validate required mappings
    const requiredFields = modelFields.filter(f => f.required).map(f => f.key);
    const mappedFields = Object.values(mapping);
    const missingRequired = requiredFields.filter(field => !mappedFields.includes(field));

    if (missingRequired.length > 0) {
      setError(`Please map the following required fields: ${missingRequired.join(', ')}`);
      return;
    }

    if (!file) {
      setError('Please select a file to import.');
      return;
    }

    importMutation.mutate({ file, mapping });
  };

  const handleClose = () => {
    setFile(null);
    setColumns([]);
    setMapping({});
    setPreviewData([]);
    setError('');
    onClose();
  };

  const handleResultClose = () => {
    setResultDialogOpen(false);
    setImportResult(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Import Catalog Frames</DialogTitle>
        <DialogContent>
          {!file ? (
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? '#f0f0f0' : 'transparent',
                '&:hover': { bgcolor: '#f5f5f5' }
              }}
            >
              <input {...getInputProps()} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Drop the file here...' : 'Drag & drop a CSV or Excel file here, or click to select'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Supported formats: .csv, .xlsx, .xls
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                File: {file.name}
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Column Mapping
              </Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Excel Column</TableCell>
                      <TableCell>Map to Field</TableCell>
                      <TableCell>Required</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((column) => (
                      <TableRow key={column}>
                        <TableCell>{column}</TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <InputLabel>Model Field</InputLabel>
                            <Select
                              value={mapping[column] || ''}
                              onChange={(e) => handleMappingChange(column, e.target.value)}
                              label="Model Field"
                            >
                              <MenuItem value="">
                                <em>Skip</em>
                              </MenuItem>
                              {modelFields.map((field) => (
                                <MenuItem key={field.key} value={field.key}>
                                  {field.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {modelFields.find(f => f.key === mapping[column])?.required ? 'Yes' : 'No'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {previewData.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Data Preview (First 5 rows)
                  </Typography>
                  <TableContainer >
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {columns.map((col) => (
                            <TableCell key={col}>{col}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.map((row, idx) => (
                          <TableRow key={idx}>
                            {columns.map((col) => (
                              <TableCell key={col}>{row[col]}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {file && (
            <Button 
              onClick={handleImport} 
              variant="contained" 
              disabled={importMutation.isLoading}
            >
              {importMutation.isLoading ? <CircularProgress size={20} /> : 'Import'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ImportResultDialog
        open={resultDialogOpen}
        onClose={handleResultClose}
        result={importResult}
      />
    </>
  );
};

export default CatalogFrameImportDialog;